import { useState } from 'react';
import { t } from '../../i18n';
import { speakerIcon, speakerOffIcon } from '../icons';
import type { HistorySummarizationEntry, IterationUsageEntry } from '../../types/iteration-usage';
import { formatHistorySummarizationLine, summarizeIterationUsage } from '../../types/iteration-usage';
import { IterationUsageModal } from './IterationUsageModal';
import { useWidgetStore } from '../../state/createWidgetStore';

export function MessageMeta({
  showFeedback,
  voiceEnabled,
  isPlaying,
  feedback,
  timeStr,
  iterationTokens,
  summarizationEvents,
  onFeedback,
  onTts,
}: {
  showFeedback: boolean;
  voiceEnabled?: boolean;
  isPlaying: boolean;
  feedback?: 'positive' | 'negative';
  timeStr: string;
  iterationTokens?: IterationUsageEntry[];
  summarizationEvents?: HistorySummarizationEntry[];
  onFeedback: (rating: 'positive' | 'negative') => void;
  onTts: () => void;
}) {
  const [showIterationModal, setShowIterationModal] = useState(false);
  const iterationSummary = summarizeIterationUsage(iterationTokens ?? []);
  const isChatDebugUsageEnabled = useWidgetStore((s) => s.config.chatDebugUsageEnabled ?? false);
  const hasSummarization = !!summarizationEvents?.length;
  const triggerLabel = iterationSummary?.label
    ?? (hasSummarization ? formatHistorySummarizationLine(summarizationEvents![0]) : undefined);

   return (
    <div className="message-meta">
      {showFeedback && (
        <div className="feedback-buttons">
          {voiceEnabled && (
            <button
              className={`tts-btn${isPlaying ? ' tts-playing' : ''}`}
              title={isPlaying ? t('stopReading') : t('readAloud')}
              onClick={onTts}
              dangerouslySetInnerHTML={{ __html: isPlaying ? speakerOffIcon : speakerIcon }}
            />
          )}
          <button
            className={`feedback-btn${feedback === 'positive' ? ' feedback-btn-active' : ''}`}
            title="Helpful"
            onClick={() => onFeedback('positive')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill={feedback === 'positive' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 10v12"/>
              <path d="M15 5.88L14 10h5.83a2 2 0 011.92 2.56l-2.33 8A2 2 0 0117.5 22H4a2 2 0 01-2-2v-8a2 2 0 012-2h2.76a2 2 0 001.79-1.11L12 2a3.13 3.13 0 013 3.88z"/>
            </svg>
          </button>
          <button
            className={`feedback-btn${feedback === 'negative' ? ' feedback-btn-active' : ''}`}
            title="Not helpful"
            onClick={() => onFeedback('negative')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill={feedback === 'negative' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 14V2"/>
              <path d="M9 18.12L10 14H4.17a2 2 0 01-1.92-2.56l2.33-8A2 2 0 016.5 2H20a2 2 0 012 2v8a2 2 0 01-2 2h-2.76a2 2 0 00-1.79 1.11L12 22a3.13 3.13 0 01-3-3.88z"/>
            </svg>
          </button>
        </div>
      )}
      <span className="message-time">{timeStr}</span>
      {(iterationSummary || hasSummarization) && isChatDebugUsageEnabled && (
        <>
          <button
            type="button"
            className="iteration-usage-trigger"
            title="View LLM runs, tools and token usage (SuperAdmin only)"
            onClick={() => setShowIterationModal(true)}
          >
            {triggerLabel}
          </button>
          {showIterationModal && (
            <IterationUsageModal
              entries={iterationTokens ?? []}
              summarizationEvents={summarizationEvents}
              onClose={() => setShowIterationModal(false)}
            />
          )}
        </>
      )}
    </div>
  );
}
